import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Input,
  Table,
  Tag,
  Space,
  Select,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { TStaff } from "~/types/staff";
import { useGetAllStaff } from "~/services/operation";
import { formatStaffName } from "~/utils/format-staff-name";
import { useDebounceValue } from "~/hooks/use-debounce";
import { useStaffStore } from "~/components/admin/operations/staffings/staffs.store";
import { ModalStaff } from "~/components/admin/operations/staffings/modal-staff/modal-staff";
import { capitalize } from "~/utils";

export const StaffManagement: React.FC = () => {
  const { filters, setFilters, openStaffModal } = useStaffStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "lead" | "driver" | "level">("name");
  const [search] = useDebounceValue(searchTerm, 500);

  useEffect(() => {
    const filters: Record<string, any> = {};
    filters["search"] = search;
    setFilters(filters);
  }, [search, setFilters]);

  const { data: staffs, isLoading } = useGetAllStaff(filters);

  // Sort staff based on selected sort option
  const sortedStaffs = useMemo(() => {
    if (!staffs?.items) return [];

    const items = [...staffs.items];

    switch (sortBy) {
      case "name":
        return items.sort((a, b) => {
          const nameA = formatStaffName(a).toLowerCase();
          const nameB = formatStaffName(b).toLowerCase();
          return nameA.localeCompare(nameB);
        });
      case "lead":
        return items.sort((a, b) => {
          if (a.lead === b.lead) return 0;
          return a.lead ? -1 : 1;
        });
      case "driver":
        return items.sort((a, b) => {
          if (a.driver === b.driver) return 0;
          return a.driver ? -1 : 1;
        });
      case "level":
        return items.sort((a, b) => {
          const levelA =
            ((a.installation ?? 0) +
              (a.packing ?? 0) +
              (a.handling ?? 0) +
              (a.clientRelations ?? 0) +
              (a.teamwork ?? 0)) /
            5;
          const levelB =
            ((b.installation ?? 0) +
              (b.packing ?? 0) +
              (b.handling ?? 0) +
              (b.clientRelations ?? 0) +
              (b.teamwork ?? 0)) /
            5;
          return levelB - levelA; // Descending order
        });
      default:
        return items;
    }
  }, [staffs, sortBy]);

  const columns: ColumnsType<TStaff> = useMemo(
    () => [
      {
        title: "Name",
        key: "name",
        render: (_, record) => (
          <Space>
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "#f0f0f0",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <UserOutlined />
            </div>
            <div>
              <div style={{ fontWeight: 500 }}>{formatStaffName(record)}</div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {record.user?.email || "-"}
              </div>
            </div>
          </Space>
        ),
      },
      {
        title: "Role",
        key: "role",
        render: (_, record) => (
          <Space>
            {record.lead && <Tag color="purple">Lead</Tag>}
            {record.driver && <Tag color="blue">Driver</Tag>}
            {!record.lead && !record.driver && <Tag>Staff</Tag>}
          </Space>
        ),
      },
      {
        title: "Phone",
        dataIndex: ["user", "phoneNumber"],
        key: "phone",
        render: (phone) => phone || "-",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status) => {
          const statusColors: Record<string, string> = {
            ACTIVE: "green",
            HIATUS: "orange",
            INACTIVE: "red",
          };
          return (
            <Tag color={statusColors[status] || "default"}>
              {capitalize(status || "ACTIVE")}
            </Tag>
          );
        },
      },
      {
        title: "Level",
        key: "level",
        render: (_, record) => {
          const level =
            ((record.installation ?? 0) +
              (record.packing ?? 0) +
              (record.handling ?? 0) +
              (record.clientRelations ?? 0) +
              (record.teamwork ?? 0)) /
            5;
          return level > 0 ? (
            <span>{level.toFixed(1)} ⭐</span>
          ) : (
            <span className="text-gray-400">No level</span>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Space>
            <Button
              icon={<UserOutlined />}
              size="small"
              onClick={() => openStaffModal(record.id, true)}
              title="View Profile"
            />
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => openStaffModal(record.id)}
              title="Edit Staff"
            />
            <Popconfirm
              title="Delete Staff"
              description="Are you sure you want to delete this staff member?"
              okText="Delete"
              cancelText="Cancel"
              onConfirm={() => {
                // TODO: Add delete mutation
                console.log("Delete staff:", record.id);
              }}
            >
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                title="Delete Staff"
              />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [openStaffModal]
  );

  return (
    <>
      <Card>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h3 style={{ fontWeight: 500, marginBottom: 4 }}>
                Staff Management
              </h3>
              <p style={{ color: "#666", fontSize: "14px", margin: 0 }}>
                Add, edit, and manage all staff members
              </p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openStaffModal()}
            >
              Add New Staff
            </Button>
          </div>

          <Space>
            <Select
              style={{ width: 150 }}
              value={sortBy}
              onChange={setSortBy}
              placeholder="Sort by"
            >
              <Select.Option value="name">A-Z (Name)</Select.Option>
              <Select.Option value="lead">Lead First</Select.Option>
              <Select.Option value="driver">Driver First</Select.Option>
              <Select.Option value="level">Star Level</Select.Option>
            </Select>
            <Input
              placeholder="Search staff..."
              prefix={<SearchOutlined />}
              style={{ width: 300 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Space>

          <Table
            rowKey={(staff) => staff.id}
            columns={columns}
            dataSource={sortedStaffs}
            pagination={{
              total: staffs?.meta.totalItems,
              pageSize: 10,
              current: filters.page,
              onChange: (page) => setFilters({ page }),
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} staff`,
            }}
            loading={isLoading}
          />
        </Space>
      </Card>

      <ModalStaff />
    </>
  );
};
